import { lazy, Suspense } from 'react';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from '../features/auth/AuthProvider';
import { AuthPage, ForgotPage } from '../features/auth/AuthPage';
import { AppShell } from '../features/layout/AppShell';
import { HomePage } from '../features/chat/HomePage';
import { ChatPage } from '../features/chat/ChatPage';
import { SitePasswordGate } from '../features/admin/Gates';
import { withReload } from '../lib/chunks';
import { RouteError } from '../components/RouteError';
import { Spinner } from '../components/ui';

// Heavier, less-used screens load on demand.
const ArtifactsPage = lazy(withReload(() => import('../features/artifacts/ArtifactsPage').then((m) => ({ default: m.ArtifactsPage }))));
const ArtifactPage = lazy(withReload(() => import('../features/artifacts/ArtifactsPage').then((m) => ({ default: m.ArtifactPage }))));
const ProjectsPage = lazy(withReload(() => import('../features/projects/ProjectsPage').then((m) => ({ default: m.ProjectsPage }))));
const ProjectPage = lazy(withReload(() => import('../features/projects/ProjectPage').then((m) => ({ default: m.ProjectPage }))));
const SettingsPage = lazy(withReload(() => import('../features/settings/SettingsPage').then((m) => ({ default: m.SettingsPage }))));
const ActivityPage = lazy(withReload(() => import('../features/agents/ActivityPage').then((m) => ({ default: m.ActivityPage }))));
const RunDetailPage = lazy(withReload(() => import('../features/agents/ActivityPage').then((m) => ({ default: m.RunDetailPage }))));
const ChatsPage = lazy(withReload(() => import('../features/chat/ChatsPage').then((m) => ({ default: m.ChatsPage }))));

const LegalPage = lazy(withReload(() => import('../features/legal/LegalPage').then((m) => ({ default: m.LegalPage }))));
const AdminPage = lazy(withReload(() => import('../features/admin/AdminPage').then((m) => ({ default: m.AdminPage }))));
const CreditUsagePage = lazy(withReload(() => import('../features/usage/CreditUsagePage').then((m) => ({ default: m.CreditUsagePage }))));
const AppUsagePage = lazy(withReload(() => import('../features/usage/AppUsagePage').then((m) => ({ default: m.AppUsagePage }))));
const PlansPage = lazy(withReload(() => import('../features/plans/PlansPage').then((m) => ({ default: m.PlansPage }))));
const ConnectorsPage = lazy(withReload(() => import('../features/settings/ConnectorsPage').then((m) => ({ default: m.ConnectorsPage }))));
const ExplorePage = lazy(withReload(() => import('../features/explore/ExplorePage').then((m) => ({ default: m.ExplorePage }))));
const ExploreDetailPage = lazy(withReload(() => import('../features/explore/ExplorePage').then((m) => ({ default: m.ExploreDetailPage }))));

const queryClient = new QueryClient({ defaultOptions: { queries: { staleTime: 15_000, refetchOnWindowFocus: false, retry: (n, e: any) => n < 2 && !(e?.status >= 400 && e?.status < 500) } } });
const S = (el: React.ReactElement) => <Suspense fallback={<div className="center-fill"><Spinner /></div>}>{el}</Suspense>;

const router = createBrowserRouter([
  { path: '/legal', element: S(<LegalPage />), errorElement: <RouteError /> },
  { path: '/legal/:slug', element: S(<LegalPage />), errorElement: <RouteError /> },
  { path: '/login', element: <AuthPage mode="login" />, errorElement: <RouteError /> },
  { path: '/register', element: <AuthPage mode="register" />, errorElement: <RouteError /> },
  { path: '/forgot', element: <ForgotPage />, errorElement: <RouteError /> },
  {
    element: <AppShell />,
    errorElement: <RouteError />,
    children: [
      { path: '/', element: <HomePage /> },
      { path: '/chat/:id', element: <ChatPage /> },
      { path: '/chats', element: S(<ChatsPage />) },
      { path: '/artifacts', element: S(<ArtifactsPage />) },
      { path: '/explore', element: S(<ExplorePage />) },
      { path: '/explore/:id', element: S(<ExploreDetailPage />) },
      { path: '/artifacts/:id', element: S(<ArtifactPage />) },
      { path: '/projects', element: S(<ProjectsPage />) },
      { path: '/projects/:id', element: S(<ProjectPage />) },
      { path: '/activity', element: S(<ActivityPage />) },
      { path: '/activity/:id', element: S(<RunDetailPage />) },
      { path: '/connectors', element: S(<ConnectorsPage />) },
      { path: '/settings', element: S(<SettingsPage />) },
      { path: '/plans', element: S(<PlansPage />) },
      { path: '/app-usage', element: S(<AppUsagePage />) },
      { path: '/credit-usage', element: S(<CreditUsagePage />) },
      { path: '/admin', element: S(<AdminPage />) },
      { path: '*', element: <HomePage /> },
    ],
  },
]);

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <SitePasswordGate><RouterProvider router={router} /></SitePasswordGate>
      </AuthProvider>
    </QueryClientProvider>
  );
}
